/**
 * Coffee Bean Clicker v2 - Ad Manager
 * Singleton that manages 6 types of rewarded ads with cooldowns.
 */

import { Capacitor } from '@capacitor/core';
import type { IAdProvider, AdReward, AdRewardType, AdStatus, AdProviderConfig } from './AdProvider.js';
import { MockAdProvider } from './MockAdProvider.js';
import { AdMobProvider } from './AdMobProvider.js';

// ===================================================
//  Predefined Ad Rewards
// ===================================================

export const AD_REWARDS: Record<string, AdReward> = {
  /** Offline earnings x3 */
  OFFLINE_X3: {
    type: 'offlineX3',
    amount: 3,
    description: 'Offline earnings x3',
  },
  /** BPS x3 for 30 seconds */
  BPS_BOOST: {
    type: 'bpsBoost',
    amount: 3,
    description: 'BPS x3 for 30s',
  },
  /** Tap power x10 for 10 seconds */
  TAP_BOOST: {
    type: 'tapBoost',
    amount: 10,
    description: 'Tap x10 for 10s',
  },
  /** Instant cash = BPS * 5 minutes */
  INSTANT_CASH: {
    type: 'instantCash',
    amount: 300, // 5 minutes in seconds
    description: 'Instant BPS x 5min',
  },
  /** Roasting reward x2 */
  ROAST_BONUS: {
    type: 'roastBonus',
    amount: 2,
    description: 'Roast reward x2',
  },
  /** Prestige golden beans +30% */
  PRESTIGE_BONUS: {
    type: 'prestigeBonus',
    amount: 1.3,
    description: 'Golden beans +30%',
  },
};

// ===================================================
//  Cooldown Config (milliseconds)
// ===================================================

const COOLDOWNS: Partial<Record<AdRewardType, number>> = {
  bpsBoost: 5 * 60 * 1000,     // 5 min
  tapBoost: 5 * 60 * 1000,     // 5 min
  instantCash: 10 * 60 * 1000, // 10 min
};

// ===================================================
//  Ad Provider Type
// ===================================================

export type AdProviderType = 'mock' | 'admob' | 'auto';

// ===================================================
//  AdManager Singleton
// ===================================================

class AdManagerClass {
  private provider: IAdProvider | null = null;
  private _config: AdProviderConfig = {};
  private _isInitialized = false;
  private autoPreloadEnabled = true;

  /** Cooldown timestamps: rewardType -> next available time (Date.now()) */
  private cooldownUntil: Map<AdRewardType, number> = new Map();

  // ── Getters ──

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  get config(): AdProviderConfig {
    return this._config;
  }

  // ── Initialize ──

  async initialize(
    providerType: AdProviderType = 'auto',
    config: AdProviderConfig = {},
  ): Promise<boolean> {
    this._config = config;

    // Auto-select provider based on platform
    if (providerType === 'auto') {
      if (Capacitor.isNativePlatform()) {
        providerType = 'admob';
      } else {
        providerType = 'mock';
      }
    }

    // Create provider
    switch (providerType) {
      case 'admob':
        this.provider = new AdMobProvider(config);
        break;
      case 'mock':
      default:
        this.provider = new MockAdProvider();
        break;
    }

    const success = await this.provider.initialize();
    if (success) {
      this._isInitialized = true;
      console.log(`[AdManager] Initialized with ${this.provider.name}`);

      if (this.autoPreloadEnabled) {
        this.preloadRewardedAd();
      }
    }

    return success;
  }

  // ── Status ──

  getStatus(): AdStatus {
    return this.provider?.status ?? 'idle';
  }

  isRewardedAdReady(): boolean {
    return this.provider?.isRewardedAdReady() ?? false;
  }

  // ── Cooldown ──

  /** Check if a reward type is on cooldown */
  isOnCooldown(rewardType: AdRewardType): boolean {
    const until = this.cooldownUntil.get(rewardType);
    if (!until) return false;
    return Date.now() < until;
  }

  /** Get remaining cooldown in seconds */
  getCooldownRemaining(rewardType: AdRewardType): number {
    const until = this.cooldownUntil.get(rewardType);
    if (!until) return 0;
    const remaining = until - Date.now();
    return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
  }

  /** Start cooldown for a reward type */
  private startCooldown(rewardType: AdRewardType): void {
    const duration = COOLDOWNS[rewardType];
    if (duration) {
      this.cooldownUntil.set(rewardType, Date.now() + duration);
    }
  }

  // ── Preload ──

  async preloadRewardedAd(rewardType?: AdRewardType): Promise<boolean> {
    if (!this.provider) {
      console.log('[AdManager] Provider not initialized');
      return false;
    }
    return this.provider.loadRewardedAd(rewardType);
  }

  // ── Show Ad ──

  /**
   * Show a rewarded ad and return the reward on success.
   * @param reward The reward definition
   * @param onReward Optional callback on reward earned
   * @param onError Optional callback on error/cancel
   */
  async showRewardedAd(
    reward: AdReward,
    onReward?: (reward: AdReward) => void,
    onError?: (error: string) => void,
  ): Promise<AdReward | null> {
    if (!this.provider) {
      const error = 'Ad provider not initialized';
      console.log(`[AdManager] ${error}`);
      onError?.(error);
      return null;
    }

    // Check cooldown
    if (this.isOnCooldown(reward.type)) {
      const remaining = this.getCooldownRemaining(reward.type);
      const error = `Cooldown active: ${remaining}s remaining`;
      console.log(`[AdManager] ${error}`);
      onError?.(error);
      return null;
    }

    // Load ad if not ready
    if (!this.provider.isRewardedAdReady()) {
      console.log('[AdManager] Ad not ready, loading...');
      const loaded = await this.provider.loadRewardedAd(reward.type);
      if (!loaded) {
        const error = 'Failed to load ad';
        console.log(`[AdManager] ${error}`);
        onError?.(error);
        return null;
      }
    }

    // Set reward and show
    this.provider.setReward(reward);
    const earnedReward = await this.provider.showRewardedAd(reward.type);

    if (earnedReward) {
      // Start cooldown for this reward type
      this.startCooldown(earnedReward.type);
      onReward?.(earnedReward);
    } else {
      onError?.('Ad not completed');
    }

    // Preload next ad
    if (this.autoPreloadEnabled) {
      this.preloadRewardedAd(reward.type);
    }

    return earnedReward;
  }

  // ── Convenience Methods ──

  /** Show ad for offline x3 earnings */
  async watchOfflineX3(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.OFFLINE_X3, onReward);
  }

  /** Show ad for BPS boost (x3 for 30s) */
  async watchBpsBoost(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.BPS_BOOST, onReward);
  }

  /** Show ad for tap boost (x10 for 10s) */
  async watchTapBoost(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.TAP_BOOST, onReward);
  }

  /** Show ad for instant cash (BPS * 5min) */
  async watchInstantCash(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.INSTANT_CASH, onReward);
  }

  /** Show ad for roast bonus (x2) */
  async watchRoastBonus(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.ROAST_BONUS, onReward);
  }

  /** Show ad for prestige bonus (+30% golden beans) */
  async watchPrestigeBonus(onReward?: (r: AdReward) => void): Promise<AdReward | null> {
    return this.showRewardedAd(AD_REWARDS.PRESTIGE_BONUS, onReward);
  }

  // ── Config ──

  setAutoPreload(enabled: boolean): void {
    this.autoPreloadEnabled = enabled;
  }

  // ── Destroy ──

  destroy(): void {
    this.provider?.dispose();
    this.provider = null;
    this._isInitialized = false;
    this.cooldownUntil.clear();
    console.log('[AdManager] Destroyed');
  }
}

// Singleton export
export const AdManager = new AdManagerClass();
